/**
 * FONTE ÚNICA de resolução de país → moeda → locale no frontend.
 *
 * Antes esta lógica estava DUPLICADA em `hooks/use-geo-pricing.ts`, `pages/api/geo.ts`
 * e `pages/api/ipwho.ts`, com risco de divergência (e foi origem de bug de cobrança:
 * usuário no Brasil sendo cobrado em USD). Tudo que precisa decidir moeda/locale por país
 * deve importar daqui.
 *
 * Regra de moeda (preservada do mapeamento histórico):
 *   - BR                         → BRL
 *   - Zona do euro + alguns EU   → EUR
 *   - Qualquer outro / desconhecido → USD
 *
 * Observação de billing: os preços no Stripe são CURADOS por moeda (R$ / € / $),
 * não conversões automáticas de câmbio. Por isso a moeda correta precisa ser
 * resolvida de forma confiável ANTES do checkout — ver `use-geo-pricing.ts`.
 */

export type Currency = "brl" | "eur" | "usd";

export const SUPPORTED_CURRENCIES: readonly Currency[] = ["brl", "eur", "usd"] as const;

/**
 * Moeda padrão quando NÃO foi possível detectar o país.
 * Produto majoritariamente brasileiro: BRL é o default mais seguro — cobrar BRL é
 * aceito amplamente, enquanto cobrar USD em cartão BR costuma falhar (origem do bug).
 */
export const DEFAULT_CURRENCY: Currency = "brl";

/** Países atendidos em EUR (inclui não-eurozona historicamente mapeados para EUR). */
const EUR_COUNTRIES = new Set<string>([
  "PT", "ES", "DE", "FR", "IT", "NL", "BE", "AT", "IE", "FI", "SE", "NO", "DK", "CH",
]);

/** Países explicitamente em BRL. */
const BRL_COUNTRIES = new Set<string>(["BR"]);

const COUNTRY_TO_LOCALE: Record<string, "pt-BR" | "es-ES" | "en-US"> = {
  BR: "pt-BR",
  PT: "pt-BR",
  ES: "es-ES",
  AR: "es-ES",
  CL: "es-ES",
  CO: "es-ES",
  MX: "es-ES",
  PE: "es-ES",
  UY: "es-ES",
  PY: "es-ES",
  BO: "es-ES",
  EC: "es-ES",
  VE: "es-ES",
  US: "en-US",
  CA: "en-US",
  GB: "en-US",
  AU: "en-US",
  DE: "en-US",
  FR: "en-US",
  IT: "en-US",
  NL: "en-US",
  BE: "en-US",
  AT: "en-US",
  IE: "en-US",
  FI: "en-US",
  SE: "en-US",
  NO: "en-US",
  DK: "en-US",
  CH: "en-US",
};

const LOCALE_TO_CURRENCY: Record<string, Currency> = {
  "pt-BR": "brl",
  pt: "brl",
  "en-US": "usd",
  en: "usd",
  "es-ES": "eur",
  es: "eur",
  de: "eur",
  fr: "eur",
  it: "eur",
};

export function isSupportedCurrency(value: unknown): value is Currency {
  return typeof value === "string" && (SUPPORTED_CURRENCIES as readonly string[]).includes(value);
}

/**
 * Resolve a moeda a partir do código de país ISO (ex.: "BR", "US").
 * Retorna `null` quando o código está vazio/desconhecido, para que o chamador
 * possa decidir entre tentar outra fonte ou usar `DEFAULT_CURRENCY`.
 */
export function resolveCurrencyFromCountry(countryCode: string | null | undefined): Currency | null {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!code) return null;
  if (BRL_COUNTRIES.has(code)) return "brl";
  if (EUR_COUNTRIES.has(code)) return "eur";
  // Países conhecidos fora de BR/EUR → USD. Códigos totalmente desconhecidos também
  // caem em USD aqui; chamadores que queiram diferenciar "desconhecido" devem checar
  // o country code antes.
  return "usd";
}

/** Resolve o locale (idioma) a partir do código de país. */
export function resolveLocaleFromCountry(
  countryCode: string | null | undefined
): "pt-BR" | "es-ES" | "en-US" | null {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!code) return null;
  return COUNTRY_TO_LOCALE[code] ?? null;
}

/** Resolve a moeda a partir do locale do navegador (fallback fraco, só pré-detecção). */
export function resolveCurrencyFromLocale(locale: string | null | undefined): Currency {
  const lang = String(locale || "").trim();
  if (!lang) return DEFAULT_CURRENCY;
  const base = lang.split("-")[0] ?? "";
  return LOCALE_TO_CURRENCY[lang] || LOCALE_TO_CURRENCY[base] || DEFAULT_CURRENCY;
}
